// External libraries
import React from 'react';
import styled from 'styled-components';

// Zocdoc libraries
import { UnstyledLink } from '@zocdoc-frontend-common/components-link';
import {
    FacebookLoginButtonTransparent,
    GoogleLoginButtonTransparent,
    AppleLoginButtonTransparent,
} from '@zocdoc-frontend-common/components-sociallogin';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { blue100, slate30 } from '@zocdoc-mezzanine/foundations-colors';
import { Button } from '@zocdoc-frontend-common/components-button';

// BWA stuff
import DoctorCardHeader from '../../components/DoctorCardHeader';
import PatientInfoForm from '../../components/PatientInfoForm';
import LazyLoginModal from '../../components/LazyLoginModal';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { slideRightToLeftAnimation } from '../../utils/transitionAnimation';

const PatientInfoPageView = ({
    appleLoginUrl,
    facebookLoginUrl,
    formattedAppointmentTime,
    googleLoginUrl,
    handleClickAppleLogin,
    handleClickFacebookLogin,
    handleClickGoogleLogin,
    locationId,
    loggedInUser,
    onClickLoginLink,
    onCloseLoginModal,
    onSignInSuccess,
    provider,
    providerId,
    returnUrl,
    shouldShowLoginModal,
    shouldShowNewUsersSSO,
    shouldTransition,
    __fakeImportLoginModal,
}) => {
    const userEmail = loggedInUser?.email;
    const canDefaultUserEmail =
        Boolean(loggedInUser?.has_password) && userEmail;
    const defaultEmail = canDefaultUserEmail ? userEmail : null;

    const loginModalProps = {
        defaultEmail,
        onCloseLoginModal,
        onSignInSuccess,
        returnUrl,
        __fakeImportLoginModal,
    };

    return (
        <MainContent shouldTransition={shouldTransition}>
            <DoctorCardHeader
                formattedAppointmentTime={formattedAppointmentTime}
                locationId={locationId}
                providerId={providerId}
            />
            <PatientInfoForm
                data-test="patient-info-form"
                provider={provider}
                providerId={providerId}
            />
            <Divider>
                <DividerLine />
                <span>or</span>
                <DividerLine />
            </Divider>
            {shouldShowNewUsersSSO && (
                <>
                    <SsoLoginButton
                        beforeClick={handleClickGoogleLogin}
                        data-test="google-login-button"
                        href={googleLoginUrl}
                    >
                        <GoogleLoginButtonTransparent />
                    </SsoLoginButton>
                    <SsoLoginButton
                        beforeClick={handleClickFacebookLogin}
                        data-test="facebook-login-button"
                        href={facebookLoginUrl}
                    >
                        <StyledFacebookLoginButton />
                    </SsoLoginButton>
                    {appleLoginUrl ? (
                        <SsoLoginButton
                            beforeClick={handleClickAppleLogin}
                            data-test="apple-login-button"
                            href={appleLoginUrl}
                        >
                            <StyledAppleLoginButton />
                        </SsoLoginButton>
                    ) : null}
                </>
            )}

            <LogInCta>
                <span>Already have an account? </span>
                <LogInButton
                    data-test="open-login-modal-button"
                    onClick={onClickLoginLink}
                    variation={Button.VARIATION.LINK}
                >
                    Log in
                </LogInButton>
            </LogInCta>
            {shouldShowLoginModal && (
                <LazyLoginModal
                    data-test="lazy-login-modal"
                    {...loginModalProps}
                />
            )}
        </MainContent>
    );
};

const SsoLoginButton = styled(UnstyledLink)`
    display: block;
`;

const MainContent = styled.div`
    width: 485px;
    max-width: calc(100% - 32px);

    margin: auto;

    margin-bottom: 40px;

    ${breakpoint.medium} {
        margin-bottom: 72px;
    }

    ${SsoLoginButton} :not(:last-of-type) {
        margin-bottom: 8px;
    }

    ${p => (p.shouldTransition ? slideRightToLeftAnimation : '')};
`;

const StyledFacebookLoginButton = styled(FacebookLoginButtonTransparent)`
    margin-top: 8px;
`;

const StyledAppleLoginButton = styled(AppleLoginButtonTransparent)`
    margin-top: 8px;
`;

const LogInCta = styled.div`
    font: 15px/24px ${FontFamily.SharpSansMedium};
    color: ${blue100};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 8px;
`;

const LogInButton = styled(Button)`
    font: 15px/24px ${FontFamily.SharpSansSemibold};
    padding-left: 4px;
    text-decoration: underline;
`;

const Divider = styled.div`
    display: flex;
    flex-direction: row;
    font: 13px ${FontFamily.SharpSansMedium};
    align-items: center;
    margin-bottom: 28px;
    width: 100%;

    span {
        padding-right: 12px;
        padding-left: 12px;
    }
`;

const DividerLine = styled.div`
    height: 0px;
    border: 1px solid ${slate30};
    width: 100%;
`;

export default PatientInfoPageView;
